import type { Logger } from "@expert/logging";

import ky, { HTTPError } from "ky";

import type { DefaultPayload } from "./types";

import { defaultHeaders, defaultTimeoutInMS } from "../types";

type AuthorizeVerizonInAcssPayload = DefaultPayload & { mdn: string };

interface AuthorizeVerizonInAcssResponse {
    authorized?: boolean;
    token?: string;
}

/**
 * - `AUTHORIZED`: `token` stands in for a PIN verification of the line; `prepareVerizonReplacement` sends it.
 * - `REQUEST_FAILED`: anything else (403 when the expert isn't enabled, 400, 500, network), which sends the
 *   expert to the manual flow.
 */
export type AuthorizeVerizonInAcssResult =
    | { outcome: "AUTHORIZED"; token: string }
    | { outcome: "REQUEST_FAILED"; errorCode: string };

/**
 * Records that the expert authorized the Verizon customer in ACSS, for a guide that sends the expert to ACSS
 * instead of asking for the account PIN. Nothing is checked with Verizon: the expert's confirmation is the check.
 */
export const authorizeVerizonInAcss = async (
    ssoAccessToken: string,
    payload: AuthorizeVerizonInAcssPayload,
    logger: Logger,
    apiBaseUrl: string,
): Promise<AuthorizeVerizonInAcssResult> => {
    try {
        const url = `${apiBaseUrl}/api/device-activation/v1/verizon/acss-authorization`;
        const resp = await ky.post(url, {
            headers: {
                ...defaultHeaders,
                Authorization: `Bearer ${ssoAccessToken}`,
            },
            json: payload,
            timeout: defaultTimeoutInMS,
        });

        const { authorized, token } = await resp.json<AuthorizeVerizonInAcssResponse>();
        if (authorized === true && token) return { outcome: "AUTHORIZED", token };

        logger.warn(
            { hasVerizonAcssToken: !!token },
            "Solve Guide | Error: Unexpected Verizon ACSS authorization response.",
        );
        return { outcome: "REQUEST_FAILED", errorCode: "" };
    } catch (error) {
        logger.warn({ error }, "Solve Guide | Error: Could not record the Verizon ACSS authorization.");

        let errorCode = "";
        if (error instanceof HTTPError) {
            try {
                const errorJson = await error.response.json<{ code?: string }>();
                errorCode = errorJson.code ?? "";
            } catch {
                errorCode = "";
            }
        }

        return { outcome: "REQUEST_FAILED", errorCode };
    }
};
